import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDevices } from '../../hooks/useDevices';
import { useAccessLogs } from '../../hooks/useAccessLogs';
import SecurityHeader from '../../components/ui/SecurityHeader';
import BottomTabNavigation from '../../components/ui/BottomTabNavigation';
import NotificationBanner from '../../components/ui/NotificationBanner';
import OverviewCard from './components/OverviewCard';
import AccessChart from './components/AccessChart';
import DeviceStatusCard from './components/DeviceStatusCard';
import AlertBanner from './components/AlertBanner';
import QuickActions from './components/QuickActions';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const Dashboard = () => {
  const navigate = useNavigate();
  
  // Use real data from Supabase hooks
  const { 
    devices, 
    loading: devicesLoading, 
    error: devicesError, 
    lockDevice,
    unlockDevice 
  } = useDevices();

  const { logs, stats: logsStats, loading: logsLoading } = useAccessLogs();
  
  // Add state for tracking which device is being controlled
  const [controllingDevice, setControllingDevice] = useState(null);
  
  const [overviewData, setOverviewData] = useState({
    activeLocks: 0,
    recentAlerts: 0,
    onlineDevices: 0,
    totalDevices: 0
  });
  const [lastUpdate, setLastUpdate] = useState(new Date());

  // Calculate overview data from real devices
  useEffect(() => {
    if (devices.length > 0) {
      const onlineDevices = devices.filter(device => device.isOnline).length;
      const activeLocks = devices.filter(device => device.isLocked && device.isOnline).length;
      const recentAlerts = logsStats?.anomalies || 0;

      setOverviewData({
        activeLocks,
        recentAlerts,
        onlineDevices,
        totalDevices: devices.length
      });
      
      setLastUpdate(new Date());
    }
  }, [devices, logsStats]);

  // Update timestamp periodically
  useEffect(() => {
    const updateInterval = setInterval(() => {
      setLastUpdate(new Date());
    }, 30000); // Update every 30 seconds

    return () => clearInterval(updateInterval);
  }, []);

  // Handle Lock Device
  const handleLockDevice = async (deviceId) => {
    try {
      setControllingDevice(deviceId);
      const result = await lockDevice(deviceId);
      
      if (result.success) {
        console.log('✅ Device locked successfully');
      } else {
        alert('Failed to lock device: ' + result.error);
      }
    } catch (err) {
      console.error('Lock error:', err);
      alert('Failed to lock device');
    } finally {
      setTimeout(() => setControllingDevice(null), 1000);
    }
  };

  // Handle Unlock Device
  const handleUnlockDevice = async (deviceId) => {
    try {
      setControllingDevice(deviceId);
      const result = await unlockDevice(deviceId);
      
      if (result.success) {
        console.log('✅ Device unlocked successfully');
      } else {
        alert('Failed to unlock device: ' + result.error);
      }
    } catch (err) {
      console.error('Unlock error:', err);
      alert('Failed to unlock device');
    } finally {
      setTimeout(() => setControllingDevice(null), 1000);
    }
  };

  const handleDeviceClick = (device) => {
    console.log('Device clicked:', device);
    // TODO: Navigate to device details or show modal
  };

  const handleRefresh = async () => {
    window.location.reload();
  };

  const formatLastUpdate = (date) => {
    return date.toLocaleTimeString('en-US', {
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  // Show loading state
  if (devicesLoading) {
    return (
      <div className="min-h-screen bg-background">
        <SecurityHeader />
        <div className="pt-16 pb-20">
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="flex flex-col items-center space-y-4">
              <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
              <p className="text-muted-foreground">Loading dashboard from Supabase...</p>
            </div>
          </div>
        </div>
        <BottomTabNavigation />
      </div>
    );
  }

  // Show error state
  if (devicesError) {
    return (
      <div className="min-h-screen bg-background">
        <SecurityHeader />
        <div className="pt-16 pb-20">
          <div className="flex items-center justify-center min-h-[60vh]">
            <div className="flex flex-col items-center space-y-4">
              <Icon name="AlertTriangle" size={48} className="text-destructive" />
              <p className="text-destructive font-semibold">Failed to load dashboard</p>
              <p className="text-muted-foreground text-sm">{devicesError}</p>
              <Button onClick={handleRefresh} iconName="RefreshCw">
                Retry
              </Button>
            </div>
          </div>
        </div>
        <BottomTabNavigation />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SecurityHeader />
      <NotificationBanner />
      <main className="pt-16 pb-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          {/* Alert Banner */}
          <AlertBanner />

          {/* Header Section */}
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Security Dashboard</h1>
              <p className="text-muted-foreground">
                Real-time IoT access control monitoring • Last updated: {formatLastUpdate(lastUpdate)}
              </p>
            </div>
            
            <div className="flex items-center space-x-3">
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                iconName="RefreshCw"
                className="hidden sm:flex"
              >
                Refresh
              </Button>
              
              <Button
                variant="default"
                size="sm"
                onClick={() => navigate('/alerts-notifications')}
                iconName="Bell"
              >
                <span className="hidden sm:inline">View Alerts</span>
                <span className="sm:hidden">Alerts</span>
              </Button>
            </div>
          </div>

          {/* Overview Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <OverviewCard
              title="Active Locks"
              value={overviewData.activeLocks}
              icon="Lock"
              trend="up"
              trendValue="+2"
              status="success"
              onClick={() => navigate('/dashboard')}
            />
            
            <OverviewCard
              title="Recent Alerts"
              value={overviewData.recentAlerts}
              icon="AlertTriangle"
              trend="down"
              trendValue="-1"
              status={overviewData.recentAlerts > 3 ? "warning" : "default"}
              onClick={() => navigate('/alerts-notifications')}
            />
            
            <OverviewCard
              title="Online Devices"
              value={`${overviewData.onlineDevices}/${overviewData.totalDevices}`}
              icon="Wifi"
              trend="stable"
              trendValue="0"
              status={overviewData.onlineDevices === overviewData.totalDevices ? "success" : "warning"}
              onClick={() => navigate('/dashboard')}
            />
            
            <OverviewCard
              title="System Health"
              value="98.5%"
              icon="Activity"
              trend="up"
              trendValue="+0.2%"
              status="success"
              onClick={() => navigate('/analytics')}
            />
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            {/* Access Chart - Takes 2 columns on large screens */}
            <div className="lg:col-span-2">
              <AccessChart />
            </div>
            
            {/* Quick Actions */}
            <div className="lg:col-span-1">
              <QuickActions />
            </div>
          </div>

          {/* Device Status Section */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-semibold text-foreground">Device Status</h2>
                <p className="text-sm text-muted-foreground">
                  Real-time lock monitoring and control • {devices.length} devices total
                </p>
              </div>
              
              <div className="flex items-center space-x-2">
                <div className="flex items-center space-x-1">
                  <div className="w-2 h-2 bg-success rounded-full security-pulse" />
                  <span className="text-xs text-muted-foreground">Live from Supabase</span>
                </div>
              </div>
            </div>

            {devices.length === 0 ? (
              <div className="bg-card border border-border rounded-lg p-8 text-center">
                <Icon name="Inbox" size={48} className="text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No devices found in database</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Add devices in Settings or check your Supabase connection
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {devices.map((device) => (
                  <DeviceStatusCard
                    key={device.id}
                    device={device}
                    onLockDevice={handleLockDevice}
                    onUnlockDevice={handleUnlockDevice}
                    onDeviceClick={handleDeviceClick}
                    isControlling={controllingDevice === device.device_id}
                  />
                ))}
              </div>
            )}
          </div>

          {/* System Status Footer */}
          <div className="bg-card border border-border rounded-lg p-4 security-shadow-card">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-success rounded-full security-pulse" />
                  <span className="text-sm text-foreground">System Operational</span>
                </div>
                
                <div className="hidden sm:flex items-center space-x-2 text-muted-foreground">
                  <Icon name="Server" size={16} />
                  <span className="text-sm">Connected to Supabase</span>
                </div>
              </div>
              
              <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                <span>Uptime: 99.8%</span>
                <span>Response: 45ms</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/analytics')}
                  iconName="BarChart3"
                  className="text-xs"
                >
                  View Metrics
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>
      <BottomTabNavigation />
    </div>
  );
};

export default Dashboard;